import React, { useContext, useState, useEffect } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import useFieldOptions from "./useFieldOptions";
import {
  findIntegrationProperty,
  findIntegrationByService,
} from "../../utils/integrationFieldTypeMap";

const FieldOptionsImportButton = ({ field }) => {
  const [contextData, builder] = useContext(VariantContextData);
  const { FieldOptions } = useFieldOptions();
  const [hidden, setHidden] = useState(false);
  const website = contextData.objects.website;
  const variant = contextData.objects.variant;

  const property = findIntegrationProperty(website, field);
  const integration = findIntegrationByService(website, field.service);
  const integrationIcon =
    integration && integration.icon
      ? `/images/integrations/${integration.icon.split("/").pop()}`
      : null;

  const active = Object.values(variant.field_options).filter(
    (option) => option.field_id == field.id && option.toBeDeleted !== true
  );
  const existingValues = active.map((option) => String(option.value));
  const propertyValues = property && Array.isArray(property.options)
    ? property.options.map((option) => String(option.value))
    : [];
  const existingSet = new Set(existingValues);
  const propertySet = new Set(propertyValues);
  const alreadyImported =
    propertyValues.length > 0 &&
    existingValues.length === propertyValues.length &&
    propertyValues.every((v) => existingSet.has(v)) &&
    existingValues.every((v) => propertySet.has(v));

  useEffect(() => {
    if (!hidden) return undefined;
    if (alreadyImported || existingValues.length >= propertyValues.length) {
      setHidden(false);
      return undefined;
    }
    const timer = setTimeout(() => setHidden(false), 2000);
    return () => clearTimeout(timer);
  }, [hidden, alreadyImported, existingValues.length, propertyValues.length]);

  if (hidden) return null;
  if (!property || !Array.isArray(property.options) || property.options.length === 0) return null;
  if (alreadyImported) return null;

  const onClick = () => {
    setHidden(true);
    if (field.field_type === "dropdown" && !field.placeholder) {
      builder.update([
        {
          object_type: "fields",
          object_id: field.id,
          setting_name: "[placeholder]",
          value: "Select an option",
        },
      ]);
    }
    active.forEach((option) => builder.remove("field_options", option.id));
    property.options.forEach((option, index) =>
      FieldOptions.create(
        field,
        {
          label: option.label || option.value,
          value: option.value,
          position: index + 1,
        },
        { skip_select: true }
      )
    );
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title="Current options don't match the integration's available options. Click to import."
      className="h-[30px] px-3 gap-2 bg-white hover:bg-slate-100 cursor-pointer border-solid border-[1px] border-slate-300 hover:border-slate-400 rounded-lg text-black text-lg font-medium inline-flex items-center"
    >
      {integrationIcon && (
        <img
          src={integrationIcon}
          alt={integration.integration_type}
          className="h-[16px] w-[16px] object-contain"
        />
      )}
      Import Options
    </button>
  );
};

export default FieldOptionsImportButton;
